import {createContext, useContext} from 'react'

export type WalkthroughSource = 'chatbot' | 'onboarding' | 'help' | 'feature_guide'

export type WalkthroughSessionMeta = {
  source: WalkthroughSource
  openChatOnFinish: boolean
}

export type WalkthroughStartOptions = Partial<WalkthroughSessionMeta>

export interface WalkthroughStep {
  // optional route path for this step (used for per-step navigation)
  path?: string
  id: string
  order: number
  execution?: 'local' | 'route' // default is 'route' if not specified
  walkthrough: {
    target: string
    title: string
    content: string
    position: 'top' | 'bottom' | 'left' | 'right' | 'center'
    highlightType: 'border' | 'spotlight' | 'glow' | 'none'
    action: 'click' | 'hover' | 'input' | 'none'
    tooltipMode?: 'blocking' | 'interactive'
    scroll: 'auto' | 'smooth' | 'none'
    stepType: 'info' | 'action' | 'warning' | 'success'
    hideNext?: boolean
    permission?: string | string[]
    completion?: {
      type: 'element' | 'route' | 'mutation'
      target: string
      timeout?: number
    }
  }
}

export interface WalkthroughState {
  isActive: boolean
  steps: WalkthroughStep[]
  currentStepIndex: number
  currentElement: HTMLElement | null
  isNavigating: boolean
  isTransitioning?: boolean
  isWaitingForModal?: boolean
  error: string | null
}

export interface WalkthroughActions {
  startWalkthrough: (
    steps: WalkthroughStep[],
    apiResponseOrOptions?: any,
    options?: WalkthroughStartOptions
  ) => Promise<void>
  nextStep: () => void
  previousStep: () => void
  goToStep: (index: number) => void
  endWalkthrough: (aborted?: boolean) => void
  pauseWalkthrough: () => void
  resumeWalkthrough: () => void
  setCurrentElement: (element: HTMLElement | null) => void
  setError: (error: string | null) => void
}

export interface DynamicWalkthroughContextValue {
  state: WalkthroughState
  actions: WalkthroughActions
  sessionMeta: WalkthroughSessionMeta
}

export const initialWalkthroughState: WalkthroughState = {
  isActive: false,
  steps: [],
  currentStepIndex: 0,
  currentElement: null,
  isNavigating: false,
  error: null,
}

export const DynamicWalkthroughContext = createContext<DynamicWalkthroughContextValue | undefined>(
  undefined
)

export function useDynamicWalkthrough(): DynamicWalkthroughContextValue {
  const context = useContext(DynamicWalkthroughContext)

  if (context === undefined) {
    throw new Error('useDynamicWalkthrough must be used within a DynamicWalkthroughProvider')
  }

  return context
}

export function getCurrentStep(state: WalkthroughState): WalkthroughStep | null {
  if (!state.isActive || state.steps.length === 0) {
    return null
  }

  return state.steps[state.currentStepIndex] || null
}

export function isLastStep(state: WalkthroughState): boolean {
  return state.currentStepIndex === state.steps.length - 1
}

export function isFirstStep(state: WalkthroughState): boolean {
  return state.currentStepIndex === 0
}

export function getProgress(state: WalkthroughState): number {
  const total = state.steps.length
  const idx = state.currentStepIndex
  if (total <= 1) return 1
  return idx / (total - 1)
}

export function getStepCountText(state: WalkthroughState): string {
  if (state.steps.length === 0) return '0 / 0'
  return `${state.currentStepIndex + 1} / ${state.steps.length}`
}
